import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PasswordInputField from '@/components/feature/PasswordInputField';
import Footer from '@/components/feature/Footer';
import Navbar from '@/components/feature/Navbar';
import LegalAcceptance from '@/components/feature/LegalAcceptance';
import { LEGAL_DOCUMENT_VERSION, legalAcceptanceMessage } from '@/shared/legal/documents';
import {
  fetchDirections,
  loginMember,
  logoutMemberSession,
  PublicApiError,
  registerMember,
  sendContactMessage,
  type PublicDirectionItem,
  uploadPublicProfileImage,
} from '@/features/public/api';
import { persistMemberSession, restoreMemberAccessToken } from '@/features/public/memberSession';
import { extractSenegalPhoneDigits, getSenegalPhoneError, toSenegalPhoneE164 } from '@/shared/phone/senegalPhone';
import { getPasswordValidationError } from '@/shared/validation/password';

type FormTab = 'adhesion' | 'login' | 'contact';

const sportOptions = ['Football', 'Basketball', 'Tennis', 'Course', 'Musculation', 'Randonnée', 'Gaming', 'Musique'];
const monthlyContributionAcceptanceMessage =
  "Veuillez accepter les conditions d'adhésion, incluant le prélèvement mensuel obligatoire de 3 000 F CFA, avant de créer votre compte.";

type AdhesionField =
  | 'firstName'
  | 'lastName'
  | 'matricule'
  | 'email'
  | 'phone'
  | 'department'
  | 'password'
  | 'confirmPassword'
  | 'acceptMonthlyContribution';
type LoginField = 'identifier' | 'password';
type ContactField = 'fullName' | 'email' | 'subject' | 'message';

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getFirstServerFieldError(error: PublicApiError, field: string) {
  const details = error.details as
    | {
        errors?: {
          fieldErrors?: Record<string, string[] | undefined>;
        };
      }
    | undefined;

  return details?.errors?.fieldErrors?.[field]?.find(Boolean);
}

function getInputClassName(hasError: boolean) {
  return `w-full px-4 py-3 rounded-lg border outline-none transition-all ${
    hasError
      ? 'border-red-300 bg-red-50/60 focus:ring-2 focus:ring-red-200 focus:border-red-400'
      : 'border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
  }`;
}

function getSelectClassName(hasError: boolean) {
  return `w-full px-4 py-3 rounded-lg border outline-none transition-all ${
    hasError
      ? 'border-red-300 bg-red-50/60 focus:ring-2 focus:ring-red-200 focus:border-red-400'
      : 'border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
  }`;
}

function SenegalPhoneInput({
  value,
  onChange,
  error,
  label,
  placeholder = '77xxxxxxx',
  required = false,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className={`flex overflow-hidden rounded-lg border transition-all ${error ? 'border-red-300 bg-red-50/60 focus-within:ring-2 focus-within:ring-red-200 focus-within:border-red-400' : 'border-gray-300 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500'}`}>
        <div className="flex items-center bg-gray-50 px-4 text-sm font-semibold text-gray-600 border-r border-gray-200">
          +221
        </div>
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          required={required}
          value={value}
          onChange={(event) => onChange(extractSenegalPhoneDigits(event.target.value))}
          className="w-full px-4 py-3 outline-none bg-transparent"
          placeholder={placeholder}
        />
      </div>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}

function resolveTab(value: string | null, intent: string | null): FormTab {
  if (value === 'login' || value === 'challenge') {
    return 'login';
  }

  if (value === 'contact') {
    return 'contact';
  }

  if (value === 'adhesion' && intent === 'signup') {
    return 'adhesion';
  }

  return 'login';
}

function resolveRedirectPath(value: string | null) {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return '/membre';
  }

  if (trimmedValue.startsWith('/') && !trimmedValue.startsWith('//')) {
    return trimmedValue;
  }

  return '/membre';
}

export default function FormsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const signupIntent = searchParams.get('intent');
  const [activeForm, setActiveForm] = useState<FormTab>(resolveTab(searchParams.get('tab'), signupIntent));
  const memberAccessSource = searchParams.get('source');
  const passwordUpdated = searchParams.get('passwordUpdated') === '1';
  const isMemberAccessFlow =
    memberAccessSource === 'member-access' || resolveTab(searchParams.get('tab'), signupIntent) !== 'contact';
  const redirectParam = searchParams.get('redirect');
  const redirectPath = resolveRedirectPath(redirectParam);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [adhesionSuccessModalOpen, setAdhesionSuccessModalOpen] = useState(false);
  const [submittingForm, setSubmittingForm] = useState<FormTab | null>(null);
  const [directions, setDirections] = useState<PublicDirectionItem[]>([]);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [adhesionAcceptedTerms, setAdhesionAcceptedTerms] = useState(false);
  const [loginAcceptedTerms, setLoginAcceptedTerms] = useState(false);
  const [legalError, setLegalError] = useState<string | undefined>();
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [adhesionForm, setAdhesionForm] = useState({
    firstName: '',
    lastName: '',
    matricule: '',
    email: '',
    phone: '',
    department: '',
    sports: [] as string[],
    password: '',
    confirmPassword: '',
    acceptMonthlyContribution: false,
  });
  const [adhesionErrors, setAdhesionErrors] = useState<Partial<Record<AdhesionField, string>>>({});
  const [loginForm, setLoginForm] = useState({
    identifier: '',
    password: '',
  });
  const [loginErrors, setLoginErrors] = useState<Partial<Record<LoginField, string>>>({});
  const [contactForm, setContactForm] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: '',
  });
  const [contactErrors, setContactErrors] = useState<Partial<Record<ContactField, string>>>({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!profilePhotoFile) {
      setProfilePhotoPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(profilePhotoFile);
    setProfilePhotoPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [profilePhotoFile]);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      try {
        const nextDirections = await fetchDirections();
        if (isMounted) {
          setDirections(nextDirections);
        }
      } catch {
        if (isMounted) {
          setDirections([]);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const nextTab = resolveTab(searchParams.get('tab'), signupIntent);
    if (nextTab !== activeForm) {
      setActiveForm(nextTab);
    }
  }, [activeForm, searchParams, signupIntent]);

  useEffect(() => {
    if (activeForm === 'login' && passwordUpdated) {
      setFeedback({
        type: 'success',
        message: 'Mot de passe mis à jour. Connectez-vous avec votre nouveau mot de passe.',
      });
    }
  }, [activeForm, passwordUpdated]);

  useEffect(() => {
    if (activeForm !== 'login') {
      return;
    }

    let isMounted = true;

    void (async () => {
      const token = await restoreMemberAccessToken();

      if (token && isMounted) {
        navigate(redirectPath);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [activeForm, navigate, redirectPath]);

  const changeForm = (form: FormTab) => {
    setActiveForm(form);
    setFeedback(null);
    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set('tab', form);

    if (form === 'adhesion') {
      nextSearchParams.set('intent', 'signup');
    }

    if (redirectParam) {
      nextSearchParams.set('redirect', redirectPath);
    }

    if (memberAccessSource === 'member-access') {
      nextSearchParams.set('source', memberAccessSource);
    }

    setSearchParams(nextSearchParams);
  };

  const toggleSport = (sport: string) => {
    setAdhesionForm((current) => ({
      ...current,
      sports: current.sports.includes(sport)
        ? current.sports.filter((item) => item !== sport)
        : [...current.sports, sport],
    }));
  };

  const updateAdhesionField = (field: AdhesionField, value: string | boolean) => {
    setAdhesionForm((current) => ({ ...current, [field]: value }));
    setAdhesionErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return { ...current, [field]: undefined };
    });
  };

  const updateLoginField = (field: LoginField, value: string) => {
    setLoginForm((current) => ({ ...current, [field]: value }));
    setLoginErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return { ...current, [field]: undefined };
    });
  };

  const updateContactField = (field: ContactField, value: string) => {
    setContactForm((current) => ({ ...current, [field]: value }));
    setContactErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return { ...current, [field]: undefined };
    });
  };

  const validateAdhesionForm = () => {
    const errors: Partial<Record<AdhesionField, string>> = {};

    if (adhesionForm.firstName.trim().length < 2) {
      errors.firstName = 'Le prénom doit contenir au moins 2 caractères.';
    }

    if (adhesionForm.lastName.trim().length < 2) {
      errors.lastName = 'Le nom doit contenir au moins 2 caractères.';
    }

    if (adhesionForm.matricule.trim() && adhesionForm.matricule.trim().length < 3) {
      errors.matricule = 'Le matricule doit contenir au moins 3 caractères.';
    }

    if (!adhesionForm.email.trim()) {
      errors.email = "L'adresse email est obligatoire.";
    } else if (!isValidEmail(adhesionForm.email.trim())) {
      errors.email = 'Merci de saisir une adresse email valide.';
    }

    const phoneError = getSenegalPhoneError(adhesionForm.phone);
    if (phoneError) {
      errors.phone = phoneError;
    }

    const passwordError = getPasswordValidationError(adhesionForm.password);
    if (passwordError) {
      errors.password = passwordError;
    }

    if (!adhesionForm.confirmPassword.trim()) {
      errors.confirmPassword = 'Merci de confirmer votre mot de passe.';
    } else if (adhesionForm.password !== adhesionForm.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas.';
    }

    if (!adhesionForm.acceptMonthlyContribution) {
      errors.acceptMonthlyContribution = monthlyContributionAcceptanceMessage;
    }

    return errors;
  };

  const validateLoginForm = () => {
    const errors: Partial<Record<LoginField, string>> = {};

    const phoneError = getSenegalPhoneError(loginForm.identifier);
    if (phoneError) {
      errors.identifier = phoneError;
    }

    if (!loginForm.password.trim()) {
      errors.password = 'Le mot de passe est obligatoire.';
    }

    return errors;
  };

  const validateContactForm = () => {
    const errors: Partial<Record<ContactField, string>> = {};

    if (contactForm.fullName.trim().length < 2) {
      errors.fullName = 'Le nom complet doit contenir au moins 2 caractères.';
    }

    if (!contactForm.email.trim()) {
      errors.email = "L'adresse email est obligatoire.";
    } else if (!isValidEmail(contactForm.email.trim())) {
      errors.email = 'Merci de saisir une adresse email valide.';
    }

    if (contactForm.subject.trim().length < 2) {
      errors.subject = "L'objet doit contenir au moins 2 caractères.";
    }

    if (contactForm.message.trim().length < 10) {
      errors.message = 'Le message doit contenir au moins 10 caractères.';
    } else if (contactForm.message.trim().length > 500) {
      errors.message = 'Le message doit contenir au maximum 500 caractères.';
    }

    return errors;
  };

  const handleAdhesionSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!adhesionAcceptedTerms) { setLegalError(legalAcceptanceMessage); return; }
    setFeedback(null);
    const nextErrors = validateAdhesionForm();
    setAdhesionErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmittingForm('adhesion');

    try {
      let uploadedAvatarUrl: string | undefined;

      if (profilePhotoFile) {
        const asset = await uploadPublicProfileImage(profilePhotoFile);
        uploadedAvatarUrl = asset.url;
      }

      await registerMember({
        acceptTerms: adhesionAcceptedTerms,
        legalVersion: LEGAL_DOCUMENT_VERSION,
        firstName: adhesionForm.firstName.trim(),
        lastName: adhesionForm.lastName.trim(),
        email: adhesionForm.email.trim(),
        matricule: adhesionForm.matricule.trim() || undefined,
        password: adhesionForm.password,
        phone: toSenegalPhoneE164(adhesionForm.phone),
        department: adhesionForm.department || undefined,
        avatarUrl: uploadedAvatarUrl,
        sports: adhesionForm.sports,
        interests: adhesionForm.sports,
        acceptMonthlyContribution: adhesionForm.acceptMonthlyContribution,
      });

      setAdhesionForm({
        firstName: '',
        lastName: '',
        matricule: '',
        email: '',
        phone: '',
        department: '',
        sports: [],
        password: '',
        confirmPassword: '',
        acceptMonthlyContribution: false,
      });
      setProfilePhotoFile(null);
      setAdhesionAcceptedTerms(false);
      setAdhesionErrors({});
      setFeedback(null);
      setAdhesionSuccessModalOpen(true);
    } catch (caughtError) {
      if (caughtError instanceof PublicApiError) {
        const emailError = getFirstServerFieldError(caughtError, 'email');
        const matriculeError = getFirstServerFieldError(caughtError, 'matricule');
        const phoneError = getFirstServerFieldError(caughtError, 'phone');
        const monthlyContributionError = getFirstServerFieldError(caughtError, 'acceptMonthlyContribution');
        const nextServerErrors: Partial<Record<AdhesionField, string>> = {};

        if (emailError) {
          nextServerErrors.email = emailError;
        }

        if (matriculeError) {
          nextServerErrors.matricule = matriculeError;
        }

        if (phoneError) {
          nextServerErrors.phone = phoneError;
        }

        if (monthlyContributionError) {
          nextServerErrors.acceptMonthlyContribution = monthlyContributionError;
        }

        if (!nextServerErrors.email && caughtError.message.toLowerCase().includes('email')) {
          nextServerErrors.email = caughtError.message;
        }

        if (!nextServerErrors.matricule && caughtError.message.toLowerCase().includes('matricule')) {
          nextServerErrors.matricule = caughtError.message;
        }

        if (!nextServerErrors.phone && caughtError.message.toLowerCase().includes('num')) {
          nextServerErrors.phone = caughtError.message;
        }

        if (
          !nextServerErrors.acceptMonthlyContribution &&
          caughtError.message.toLowerCase().includes('conditions')
        ) {
          nextServerErrors.acceptMonthlyContribution = caughtError.message;
        }

        if (Object.keys(nextServerErrors).length > 0) {
          setAdhesionErrors((current) => ({ ...current, ...nextServerErrors }));
          return;
        }
      }

      const message = caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer la demande d'adhésion.";
      setFeedback({
        type: 'error',
        message,
      });
    } finally {
      setSubmittingForm(null);
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!loginAcceptedTerms) { setLegalError(legalAcceptanceMessage); return; }
    setFeedback(null);
    const nextErrors = validateLoginForm();
    setLoginErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmittingForm('login');

    try {
      const payload = await loginMember({
        acceptTerms: loginAcceptedTerms,
        legalVersion: LEGAL_DOCUMENT_VERSION,
        identifier: toSenegalPhoneE164(loginForm.identifier),
        password: loginForm.password,
      });

      if (payload.user.role === 'ADMIN') {
        await logoutMemberSession().catch(() => undefined);
        setFeedback({
          type: 'error',
          message: "Le compte administrateur se connecte uniquement depuis l'interface admin.",
        });
        return;
      }

      persistMemberSession(payload.accessToken, payload.user);

      setFeedback({
        type: 'success',
        message: 'Connexion réussie.',
      });
      setLoginErrors({});

      navigate(payload.user.mustChangePassword ? '/membre' : redirectPath);
    } catch (caughtError) {
      if (caughtError instanceof PublicApiError) {
        const identifierError = getFirstServerFieldError(caughtError, 'identifier');
        const passwordError = getFirstServerFieldError(caughtError, 'password');

        if (identifierError || passwordError) {
          setLoginErrors({
            ...(identifierError ? { identifier: identifierError } : {}),
            ...(passwordError ? { password: passwordError } : {}),
          });
          return;
        }
      }

      const message = caughtError instanceof Error ? caughtError.message : "Connexion impossible.";
      setFeedback({
        type: 'error',
        message,
      });
    } finally {
      setSubmittingForm(null);
    }
  };

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    const nextErrors = validateContactForm();
    setContactErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmittingForm('contact');

    try {
      await sendContactMessage({
        fullName: contactForm.fullName.trim(),
        email: contactForm.email.trim(),
        subject: contactForm.subject.trim(),
        message: contactForm.message.trim(),
      });

      setContactForm({
        fullName: '',
        email: '',
        subject: '',
        message: '',
      });
      setContactErrors({});
      setFeedback({
        type: 'success',
        message: 'Ton message a bien ete envoye. Nous te repondrons tres vite.',
      });
    } catch (caughtError) {
      if (caughtError instanceof PublicApiError) {
        const fullNameError = getFirstServerFieldError(caughtError, 'fullName');
        const emailError = getFirstServerFieldError(caughtError, 'email');
        const subjectError = getFirstServerFieldError(caughtError, 'subject');
        const messageError = getFirstServerFieldError(caughtError, 'message');

        if (fullNameError || emailError || subjectError || messageError) {
          setContactErrors({
            ...(fullNameError ? { fullName: fullNameError } : {}),
            ...(emailError ? { email: emailError } : {}),
            ...(subjectError ? { subject: subjectError } : {}),
            ...(messageError ? { message: messageError } : {}),
          });
          return;
        }
      }

      setFeedback({
        type: 'error',
        message: caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer le message.",
      });
    } finally {
      setSubmittingForm(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {adhesionSuccessModalOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <i className="ri-checkbox-circle-line text-4xl"></i>
            </div>
            <h2 className="text-center text-2xl font-bold text-gray-900">Demande envoyée avec succès</h2>
            <p className="mt-4 text-center text-sm leading-6 text-gray-600">
              Votre demande d&apos;adhésion a bien été soumise. Nous vous enverrons un email dès que l&apos;administrateur aura validé votre compte.
            </p>
            <p className="mt-2 text-center text-sm leading-6 text-gray-500">
              Après validation, vous pourrez vous connecter à votre espace membre avec vos identifiants.
            </p>
            <button
              type="button"
              onClick={() => {
                setAdhesionSuccessModalOpen(false);
                changeForm('login');
              }}
              className="mt-6 w-full rounded-xl bg-[#E8521A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16]"
            >
              Revenir à la connexion
            </button>
          </div>
        </div>
      ) : null}

      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=african%20office%20workers%20filling%20forms%20documents%20senegal%20professional%20modern%20workspace%20teal%20branding%20clean%20minimal&width=1600&height=300&seq=forms-hero1&orientation=landscape"
            alt="Formulaires ASC"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1A8C7A]/85"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {!isMemberAccessFlow ? (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour
            </button>
          ) : null}
          <div className="text-center text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {isMemberAccessFlow
                ? activeForm === 'adhesion'
                  ? "Créer un compte d'adhésion"
                  : 'Connexion membre'
                : 'Formulaires ASC'}
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              {isMemberAccessFlow
                ? activeForm === 'adhesion'
                  ? "Crée ton compte ici. Après validation par l'administrateur, tu pourras revenir te connecter à ton espace membre."
                  : "Connecte-toi à ton espace membre. Si tu n'as pas encore de compte, utilise le lien de création d'adhésion juste en dessous."
                : 'Adhésion, connexion ou contact - tous nos formulaires essentiels en un seul endroit'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!isMemberAccessFlow ? (
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              { id: 'adhesion', label: 'Adhésion', icon: 'ri-user-add-line' },
              { id: 'login', label: 'Connexion', icon: 'ri-login-box-line' },
              { id: 'contact', label: 'Contact', icon: 'ri-mail-line' },
            ].map((form) => (
              <button
                key={form.id}
                onClick={() => changeForm(form.id as FormTab)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeForm === form.id
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <i className={form.icon}></i>
                {form.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {feedback ? (
            <div
              className={`mb-6 rounded-xl px-4 py-3 text-sm ${
                feedback.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-100 text-emerald-700'
                  : 'bg-red-50 border border-red-100 text-red-600'
              }`}
            >
              {feedback.message}
            </div>
          ) : null}

          {activeForm === 'adhesion' && (
            <form className="space-y-6" onSubmit={handleAdhesionSubmit}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Rejoindre l&apos;ASC</h2>
                <p className="text-gray-600">
                  Crée ton compte membre ici. Il sera ensuite validé par l&apos;administrateur avant ta première connexion.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                  <input
                    type="text"
                    required
                    value={adhesionForm.firstName}
                    onChange={(event) => updateAdhesionField('firstName', event.target.value)}
                    className={getInputClassName(Boolean(adhesionErrors.firstName))}
                    placeholder="Votre prénom"
                  />
                  {adhesionErrors.firstName ? <p className="mt-2 text-sm text-red-600">{adhesionErrors.firstName}</p> : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={adhesionForm.lastName}
                    onChange={(event) => updateAdhesionField('lastName', event.target.value)}
                    className={getInputClassName(Boolean(adhesionErrors.lastName))}
                    placeholder="Votre nom"
                  />
                  {adhesionErrors.lastName ? <p className="mt-2 text-sm text-red-600">{adhesionErrors.lastName}</p> : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Matricule SONATEL</label>
                  <input
                    type="text"
                    value={adhesionForm.matricule}
                    onChange={(event) => updateAdhesionField('matricule', event.target.value)}
                    className={getInputClassName(Boolean(adhesionErrors.matricule))}
                    placeholder="Ex: SON-2025-XXX"
                  />
                  {adhesionErrors.matricule ? <p className="mt-2 text-sm text-red-600">{adhesionErrors.matricule}</p> : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={adhesionForm.email}
                    onChange={(event) => updateAdhesionField('email', event.target.value)}
                    className={getInputClassName(Boolean(adhesionErrors.email))}
                    placeholder="prenom.nom@sonatel.sn"
                  />
                  {adhesionErrors.email ? <p className="mt-2 text-sm text-red-600">{adhesionErrors.email}</p> : null}
                </div>
                <SenegalPhoneInput
                  label="Téléphone *"
                  required
                  value={adhesionForm.phone}
                  onChange={(value) => updateAdhesionField('phone', value)}
                  error={adhesionErrors.phone}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
                  <select
                    value={adhesionForm.department}
                    onChange={(event) => updateAdhesionField('department', event.target.value)}
                    className={getSelectClassName(Boolean(adhesionErrors.department))}
                  >
                    <option value="">Sélectionnez votre direction</option>
                    {directions.map((direction) => (
                      <option key={direction.id} value={direction.nom}>{direction.nom}</option>
                    ))}
                  </select>
                  {adhesionErrors.department ? <p className="mt-2 text-sm text-red-600">{adhesionErrors.department}</p> : null}
                </div>
                <PasswordInputField
                  label="Mot de passe *"
                  required
                  value={adhesionForm.password}
                  onChange={(event) => updateAdhesionField('password', event.target.value)}
                  className={getInputClassName(Boolean(adhesionErrors.password))}
                  placeholder="Au moins 8 caractères, une majuscule et un chiffre"
                  error={adhesionErrors.password}
                  autoComplete="new-password"
                />
                <PasswordInputField
                  label="Confirmer le mot de passe *"
                  required
                  value={adhesionForm.confirmPassword}
                  onChange={(event) => updateAdhesionField('confirmPassword', event.target.value)}
                  className={getInputClassName(Boolean(adhesionErrors.confirmPassword))}
                  placeholder="Retapez votre mot de passe"
                  error={adhesionErrors.confirmPassword}
                  autoComplete="new-password"
                />
              </div>

              <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Photo de profil</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Optionnel. Tu peux déjà charger ta photo ici pour ton futur espace membre.
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-[#E8521A] border border-orange-200 hover:bg-orange-50 cursor-pointer">
                    <i className="ri-upload-2-line"></i>
                    Choisir une photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        setProfilePhotoFile(file);
                      }}
                    />
                  </label>
                </div>

                <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden border border-orange-100 bg-white flex items-center justify-center text-gray-300">
                    {profilePhotoPreview ? (
                      <img src={profilePhotoPreview} alt="Aperçu de la photo de profil" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-user-3-line text-3xl"></i>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      {profilePhotoFile ? profilePhotoFile.name : "Aucune photo sélectionnée"}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Formats image acceptés. Cette étape est facultative.
                    </p>
                  </div>
                  {profilePhotoFile ? (
                    <button
                      type="button"
                      onClick={() => setProfilePhotoFile(null)}
                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-white"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Retirer
                    </button>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sports/Activités d&apos;intérêt</label>
                <div className="flex flex-wrap gap-3">
                  {sportOptions.map((sport) => (
                    <label key={sport} className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={adhesionForm.sports.includes(sport)}
                        onChange={() => toggleSport(sport)}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm">{sport}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-orange-50 rounded-xl p-4">
                <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  Workflow d&apos;accès
                </h4>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• Tu crées d&apos;abord ton compte ici</li>
                  <li>• Ton compte passe ensuite en attente de validation admin</li>
                  <li>• Dès validation, tu peux te connecter et accéder à ton espace membre</li>
                </ul>
              </div>

              <div
                className={`rounded-xl border p-4 ${
                  adhesionErrors.acceptMonthlyContribution
                    ? 'border-red-200 bg-red-50'
                    : 'border-orange-200 bg-orange-50/60'
                }`}
              >
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={adhesionForm.acceptMonthlyContribution}
                    onChange={(event) => updateAdhesionField('acceptMonthlyContribution', event.target.checked)}
                    className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    aria-invalid={Boolean(adhesionErrors.acceptMonthlyContribution)}
                  />
                  <span>
                    J&apos;accepte les conditions d&apos;adhésion et le prélèvement mensuel obligatoire de{' '}
                    <span className="font-semibold">3 000 F CFA</span>.
                  </span>
                </label>
                {adhesionErrors.acceptMonthlyContribution ? (
                  <p className="mt-3 text-sm text-red-600">{adhesionErrors.acceptMonthlyContribution}</p>
                ) : null}
              </div>

              <LegalAcceptance id="adhesion-legal" checked={adhesionAcceptedTerms}
                onChange={(checked) => { setAdhesionAcceptedTerms(checked); setLegalError(undefined); }} error={legalError} />
              <button
                type="submit"
                disabled={submittingForm === 'adhesion' || !adhesionAcceptedTerms}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submittingForm === 'adhesion' ? 'Création du compte...' : "Créer mon compte d'adhésion"}
              </button>

              {isMemberAccessFlow ? (
                <p className="text-center text-gray-600">
                  Déjà un compte ?{' '}
                  <button type="button" onClick={() => changeForm('login')} className="text-orange-600 font-medium hover:underline">
                    Revenir à la connexion
                  </button>
                </p>
              ) : null}
            </form>
          )}

          {activeForm === 'login' && (
            <form className="space-y-6" onSubmit={handleLoginSubmit}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Connexion</h2>
                <p className="text-gray-600">
                  Connecte-toi pour accéder à ton espace membre. Si ton compte n&apos;est pas encore validé, l&apos;admin doit d&apos;abord l&apos;accepter.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <SenegalPhoneInput
                    label="Numéro de téléphone"
                    required
                    value={loginForm.identifier}
                    onChange={(value) => updateLoginField('identifier', value)}
                    error={loginErrors.identifier}
                  />
                </div>
                <PasswordInputField
                  label="Mot de passe"
                  required
                  value={loginForm.password}
                  onChange={(event) => updateLoginField('password', event.target.value)}
                  className={getInputClassName(Boolean(loginErrors.password))}
                  placeholder="Votre mot de passe"
                  error={loginErrors.password}
                  autoComplete="current-password"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate('/mot-de-passe-oublie')}
                    className="text-sm font-medium text-orange-600 hover:underline"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>

              <LegalAcceptance id="login-legal" checked={loginAcceptedTerms}
                onChange={(checked) => { setLoginAcceptedTerms(checked); setLegalError(undefined); }} error={legalError} />
              <button
                type="submit"
                disabled={submittingForm === 'login' || !loginAcceptedTerms}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submittingForm === 'login' ? 'Connexion en cours...' : 'Se connecter'}
              </button>

              <p className="text-center text-gray-600">
                Pas encore membre ?{' '}
                <button type="button" onClick={() => changeForm('adhesion')} className="text-orange-600 font-medium hover:underline">
                  Créer mon compte d&apos;adhésion
                </button>
              </p>
            </form>
          )}

          {activeForm === 'contact' && (
            <form className="space-y-6" onSubmit={handleContactSubmit}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Contactez-nous</h2>
                <p className="text-gray-600">Une question ? Ce formulaire envoie maintenant directement votre message au support ASC.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.fullName}
                    onChange={(event) => updateContactField('fullName', event.target.value)}
                    className={getInputClassName(Boolean(contactErrors.fullName))}
                    placeholder="Votre nom"
                  />
                  {contactErrors.fullName ? <p className="mt-2 text-sm text-red-600">{contactErrors.fullName}</p> : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(event) => updateContactField('email', event.target.value)}
                    className={getInputClassName(Boolean(contactErrors.email))}
                    placeholder="Votre email"
                  />
                  {contactErrors.email ? <p className="mt-2 text-sm text-red-600">{contactErrors.email}</p> : null}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet *</label>
                <select
                  required
                  value={contactForm.subject}
                  onChange={(event) => updateContactField('subject', event.target.value)}
                  className={getSelectClassName(Boolean(contactErrors.subject))}
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="Demande d'information">Demande d&apos;information</option>
                  <option value="Suggestion">Suggestion</option>
                  <option value="Reclamation">Reclamation</option>
                  <option value="Partenariat">Partenariat</option>
                  <option value="Autre">Autre</option>
                </select>
                {contactErrors.subject ? <p className="mt-2 text-sm text-red-600">{contactErrors.subject}</p> : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  required
                  rows={5}
                  maxLength={500}
                  value={contactForm.message}
                  onChange={(event) => updateContactField('message', event.target.value)}
                  className={`${getInputClassName(Boolean(contactErrors.message))} resize-none`}
                  placeholder="Votre message..."
                ></textarea>
                {contactErrors.message ? <p className="mt-2 text-sm text-red-600">{contactErrors.message}</p> : null}
                <p className="text-sm text-gray-500 mt-1">Maximum 500 caractères</p>
              </div>

              <button
                type="submit"
                disabled={submittingForm === 'contact'}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submittingForm === 'contact' ? 'Envoi en cours...' : 'Envoyer le message'}
              </button>
            </form>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
